import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { ArrowIcon, Button as Button95, Checkbox, Fieldset, Hourglass, NumberInput, Panel, Radio, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useLocalSearchParams, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Meta } from '@/components/Meta';
import { Image } from 'expo-image';
import { Children, ChildrenNames, Ethnicity, EthnicityNames, FamilyPlan, FamilyPlanNames, Intention, IntentionNames, Politics, PoliticsNames, RelationshipType, RelationshipTypeNames } from '@/types/profile';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: '100%',
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
    width: '100%',
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
  },
  numeric: {
    width: 80,
  },
  flexRow: {
    flexDirection: "row",
    gap: 40,
    alignItems: "center",
    alignSelf: "center",
    flexWrap: "wrap",
  },
});

export default function PoliticsScreen() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const { edit } = useLocalSearchParams<{ edit?: string }>();

  const [politicsValue, setPoliticsValue] = useState(communityMembership.profile.politics ?? 0);

  const onSubmit = useCallback(
    async (skip: boolean) => {
      try {
        showSpinner();

        const response = await AppUtilities.fetchAuthorized(
          "PoliticsScreen.onSubmit",
          "/rabbl/api/optionalsettings",
          "POST",
          {
            domain: domain,
            politics: 1,
            politicsValue: politicsValue,
          }
        );
        const membership: CommunityMembership = ((response as any).body as any).community_membership;
        dispatch(setData(response));
        if (edit && edit.length > 0 && router.canGoBack()) {
          router.back();
        } else {
          router.navigate(AppUtils.getProfileCreateNextOptionalPageLink("politics", membership) as RelativePathString);
        }

      } catch (e) {
        AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(skip), true);
        AppUtilities.handleException(e, "PoliticsScreen.onSubmit");
      } finally {
        hideSpinner();
      }
    },
    [
      router,
      domain,
      politicsValue,
    ]
  );

  return (
    <BasicScrollView center={true}>
      <Meta title={"Politics"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>

          <CustomText>Your politics</CustomText>

          <Radio
            status={politicsValue == Politics.POLITICS_NONE ? 'checked' : 'unchecked'}
            onPress={() => setPoliticsValue(Politics.POLITICS_NONE)}
            label={PoliticsNames.get(Politics.POLITICS_NONE)}
          />

          <Radio
            status={politicsValue == Politics.POLITICS_NOT_POLITICAL ? 'checked' : 'unchecked'}
            onPress={() => setPoliticsValue(Politics.POLITICS_NOT_POLITICAL)}
            label={PoliticsNames.get(Politics.POLITICS_NOT_POLITICAL)}
          />

          <Radio
            status={politicsValue == Politics.POLITICS_MODERATE ? 'checked' : 'unchecked'}
            onPress={() => setPoliticsValue(Politics.POLITICS_MODERATE)}
            label={PoliticsNames.get(Politics.POLITICS_MODERATE)}
          />

          <Radio
            status={politicsValue == Politics.POLITICS_CONSERVATIVE ? 'checked' : 'unchecked'}
            onPress={() => setPoliticsValue(Politics.POLITICS_CONSERVATIVE)}
            label={PoliticsNames.get(Politics.POLITICS_CONSERVATIVE)}
          />

          <Radio
            status={politicsValue == Politics.POLITICS_LIBERAL ? 'checked' : 'unchecked'}
            onPress={() => setPoliticsValue(Politics.POLITICS_LIBERAL)}
            label={PoliticsNames.get(Politics.POLITICS_LIBERAL)}
          />

          <Radio
            status={politicsValue == Politics.POLITICS_OTHER ? 'checked' : 'unchecked'}
            onPress={() => setPoliticsValue(Politics.POLITICS_OTHER)}
            label={PoliticsNames.get(Politics.POLITICS_OTHER)}
          />

          <View style={styles.flexRow}>
            <View style={styles.buttonWrapper}>
              <Button95
                onPress={() => onSubmit(false)}
              >{ edit ? "Save" : "Next" }</Button95>
            </View>

            {
              (!edit || edit.length == 0) && (
                <>
                  <View style={styles.buttonWrapper}>
                    <Button95
                      onPress={() => onSubmit(true)}
                    >Skip</Button95>
                  </View>
                  <View style={styles.buttonWrapper}>
                    <Button95
                      onPress={() => router.navigate(AppUtils.getProfileCreateNextPageLink(communityMembership) as RelativePathString)}
                    >Skip all</Button95>
                  </View>
                </>
              )
            }
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
